import { useCallback, useEffect, useRef, useState } from "react";
import { api, errDetail } from "@/lib/api";

// R17 - load ONE scoped report view (`path?qs`) with request identity: the previous figures are cleared the moment the scope (qs) changes so no number is ever shown beside a different date or product,
// and a response (or error) from anything but the latest request is dropped. A failure is an explicit error (never zeros, never an old view). `reload()` retries.
const isCancelled = (e) => e && (e.code === "ERR_CANCELED" || e.name === "CanceledError" || e.name === "AbortError");

export function useScopedReport(path, qs, { enabled = true } = {}) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const gen = useRef(0);
  const ctl = useRef(null);

  const load = useCallback(() => {
    const mine = ++gen.current;
    if (ctl.current) ctl.current.abort();
    if (!enabled) { setData(null); setError(null); setLoading(false); return; }
    const c = new AbortController();
    ctl.current = c;
    setData(null); setError(null); setLoading(true);
    api.get(`${path}?${qs}`, { signal: c.signal })
      .then((r) => { if (mine === gen.current) { setData(r.data); setLoading(false); } })
      .catch((e) => { if (mine === gen.current && !isCancelled(e)) { setError(errDetail(e, "Could not load this view")); setLoading(false); } });
  }, [path, qs, enabled]);

  useEffect(() => { load(); return () => { gen.current += 1; if (ctl.current) ctl.current.abort(); }; }, [load]);
  return { data, error, loading, reload: load };
}

export const fetchRowsPage = async (path, qs, extra, page, limit = 50) => {
  const params = new URLSearchParams(qs);
  for (const [k, v] of Object.entries(extra || {})) if (v !== undefined && v !== null && v !== "") params.set(k, v);
  params.set("page", String(page)); params.set("limit", String(limit));
  const { data } = await api.get(`${path}?${params.toString()}`);
  return data;
};
